import {
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  CloudArrowUpIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline'
import { GlobalConfigType, setLoanData } from 'actions'
import cloneDeep from 'clone-deep'
import { AIPromptHistory, IAIPromptHistory } from 'components/AIPromptHistory'
import { LayoutLoading } from 'components/LayoutLoading'
import { Overview } from 'components/Overview'
import {
  AccountType,
  BaseFile,
  COMPANY_NAME_FC,
  FieldOrigin,
  getMappedProductType,
  InputType,
  INVALID_ALL_INPUTS,
  ProductTypes,
  userHasAccountType,
} from 'config'
import { usePermissions } from 'hooks/usePermissions'
import type { ReviewFormCategory, ReviewFormTemplate } from 'pages/Admin'
import {
  fillReviewFormText,
  reviewFormPlaceholderValues,
} from 'pages/Admin/ConditionsAndTemplates/ReviewFormItems/placeholders'
import { calculateLoanLimits } from 'pages/LoanStructure/Logic'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import {
  deleteReviewForm,
  downloadFile,
  getAdminConfig,
  getAppraisalReviewPdf,
  getReviewForm,
  getReviewFormReporterSection,
  updateLoanFields,
  updateReviewForm,
  updateReviewFormData,
} from 'services'
import { svgLoading } from 'stories/assets'
import {
  Button,
  ButtonGroup,
  Checkbox,
  TextArea,
  Toggle,
  Tooltip,
} from 'stories/components'
import { confirm, InputConvert, InputValidate, openAuditLog } from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { AddAppraiserFromReviewModal } from './AddAppraiserFromReviewModal'
import { AddReviewFormModal } from './AddReviewFormModal'
import { AIAutoFillModal } from './AIAutoFillModal'
import {
  type IAppraisalReviewFieldsConfig,
  type ReviewFormReporterSection,
  appraisalReviewOptionalFieldKeys,
  buildReportInputs,
  defaultInputs,
  elingtonValuationTypes,
  reportCheckKey,
  reportInfoKey,
  reversedEllingtonValuationTypes,
} from './constants'
import { ImportAppraiserModal } from './ImportAppraiserModal'

export interface ReviewFormData extends ReviewFormTemplate {
  isChecked?: boolean
  isDeleted?: boolean
  comment?: string
  aiHistory?: IAIPromptHistory[]
  aiPrompt?: string
  aiFiles?: BaseFile[]
}

const ReviewForm = ({ child = false }) => {
  const [loading, setLoading] = useState(true)
  const [loadingByItem, setLoadingByItem] = useState<Record<number, boolean>>({})
  const [inputs, setInputs] = useState<Record<string, InputType>>({})
  const [orgData, setOrgData] = useState<Record<string, any>>({})
  const [addressMap, setAddressMap] = useState<Record<string, string>>({})
  const [currentId, setCurrentId] = useState(0)
  const [categories, setCategories] = useState<ReviewFormCategory[] | null>(null)
  const [isShowAddModal, setShowAddModal] = useState(false)
  const [selectedData, setSelectedData] = useState<ReviewFormData | null>(null)
  const [isUploadStore, setUploadStore] = useState(false)
  const [overviewData, setOverviewData] = useState<any>({})
  const [isShowAddAppraiserModal, setShowAddAppraiserModal] = useState(false)
  const [isShowImportAppraiserModal, setShowImportAppraiserModal] = useState(false)
  const [isShowAIAutoFillModal, setShowAIAutoFillModal] = useState(false)
  const fieldConfigRef = useRef<IAppraisalReviewFieldsConfig>({})
  const reporterSectionRef = useRef<ReviewFormReporterSection | null>(null)

  const { hasPermission } = usePermissions()

  const canAiReview = hasPermission('Loan.Underwriting.AppraisalReview.AIReview')

  const dispatch = useDispatch()
  const { auth, loan, loanDetail, fieldProperties } = useSelector((state: any) => ({
    auth: state.auth,
    loan: state.loan,
    loanDetail: state.loanDetail,
    fieldProperties: (state.globalConfigReducer[GlobalConfigType.LoanFields] || {})[FieldOrigin.LoanStructure] || {},
  }))
  const isAdmin: boolean = userHasAccountType(auth.profile, AccountType.ADMIN)
  const placeholderValues = useMemo(() => reviewFormPlaceholderValues(loan), [loan])

  const applyFieldConfig = (inputsMap: Record<string, InputType>, config: IAppraisalReviewFieldsConfig) => {
    Object.keys(inputsMap).forEach((key) => {
      if (key.startsWith('__report_') || ['data', 'label1'].includes(key)) return
      const cfg = config[key]
      const isOptional = (appraisalReviewOptionalFieldKeys as readonly string[]).includes(key)
      if (cfg?.visible !== undefined) inputsMap[key].visible = cfg.visible !== false
      else if (isOptional) inputsMap[key].visible = false
      if (cfg?.required !== undefined) inputsMap[key].required = !!cfg.required
    })
  }

  useEffect(() => {
    setLoanNumber()
    Promise.all([
      getAdminConfig('appraisalReviewFieldsConfig').catch(() => ({})),
      getReviewFormReporterSection(),
    ]).then(([cfg, v]) => {
      const config = cfg && typeof cfg === 'object' ? (cfg as IAppraisalReviewFieldsConfig) : {}
      fieldConfigRef.current = config
      reporterSectionRef.current = v
      fetchReviewForm(0)
    })
  }, [])

  const formCount = useMemo(() => Object.keys(addressMap).length, [addressMap])

  const fetchReviewForm = (index: number) => {
    setLoading(true)

    getReviewForm(index)
      .then(
        ({
          data,
          addressMap,
          lastAutofillFiles,
          categories,
        }: {
          data: Record<string, any>
          addressMap: Record<string, string>
          lastAutofillFiles: BaseFile[]
          categories: ReviewFormCategory[]
        }) => {
          setOrgData({
            ...data,
            lastAutofillFiles,
          })
          setAddressMap(addressMap)
          const baseInputs = cloneDeep(defaultInputs())
          const rs = reporterSectionRef.current
          if (rs) {
            baseInputs.label1.title = rs.label1
          }

          const reportInputs = rs ? buildReportInputs(rs.reports || [], data?.reports || {}) : {}
          const newInputs: Record<string, InputType> = {}
          for (const key of Object.keys(baseInputs)) {
            newInputs[key] = baseInputs[key]
            if (key === 'label1') Object.assign(newInputs, reportInputs)
          }

          for (const key in data) {
            if (newInputs[key]) newInputs[key].value = data[key]
          }

          const isFixAndGroup = [ProductTypes.FixAndFlip, ProductTypes.GroundUpConstruction].includes(
            getMappedProductType(loan.productType, fieldProperties) as ProductTypes,
          )
          newInputs.appraiserAfterRepairProperty.visible = isFixAndGroup
          newInputs.appraiserARVValuationType.visible = isFixAndGroup

          applyFieldConfig(newInputs, fieldConfigRef.current)

          // Product-type rules win over config for ARV fields when not Fix/Flip/GUC
          if (!isFixAndGroup) {
            newInputs.appraiserAfterRepairProperty.visible = false
            newInputs.appraiserARVValuationType.visible = false
          }

          Object.keys(newInputs).forEach((key) => {
            if (
              !['data', 'label1'].includes(key) &&
              newInputs[key].inputType !== 'section' &&
              !key.startsWith('__report_')
            ) {
              newInputs[key].history = true
            }
          })

          setInputs(newInputs)
          setCurrentId(data.id)
          setCategories(categories)
          setLoadingByItem({})
        },
      )
      .finally(() => setLoading(false))
  }

  const onCreateForm = () => {
    fetchReviewForm(formCount)
  }

  const onDeleteForm = async () => {
    const title = inputs.title.value
    const address = inputs.propertyAddress.value
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Are you sure to remove this form?
        <br />
        <span className="text-gray-600">Title: {title}</span>
        <br />
        <span className="text-gray-600">Address: {address}</span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    setLoading(true)
    await deleteReviewForm(currentId)
    fetchReviewForm(0)
  }

  const isTitleDuplicate = (id: number, title: string) => {
    const found = inputs.data.value.find((item: ReviewFormData) => item.title.trim() == title.trim())
    if (!found) return false
    return found.id != id
  }

  const onChange = (key: string, value: any) => {
    let newInputs = cloneDeep(inputs)
    newInputs[key].value = InputConvert(newInputs[key], value)
    newInputs[key].error = ''

    if (key === 'productCode') {
      newInputs.appraiserAsIsValuationType.value = elingtonValuationTypes[value]
      newInputs.appraiserAsIsValuationType.error = ''
    } else if (key === 'appraiserAsIsValuationType') {
      const temp = reversedEllingtonValuationTypes[value]
      if (temp) {
        newInputs.productCode.value = temp
        newInputs.productCode.error = ''
      }
    }
    setInputs(newInputs)
  }

  const onBlur = async (key: string) => {
    const newInputs = cloneDeep(inputs)
    const input: any = newInputs[key]
    let error = InputValidate(input)
    input.error = error
    setInputs(newInputs)

    if (input.error && input.error.length > 0) return

    if (key.startsWith('__report_')) {
      const rs = reporterSectionRef.current
      if (!rs) return
      const reportsObj: Record<string, { check: boolean; info: string }> = {}
      rs.reports.forEach((r) => {
        reportsObj[r.id] = {
          check: !!newInputs[reportCheckKey(r.id)]?.value,
          info: (newInputs[reportInfoKey(r.id)]?.value as string) || '',
        }
      })
      setLoading(true)
      await updateReviewForm(currentId, 'reports', reportsObj)
      setOrgData({ ...orgData, reports: reportsObj })
      setLoading(false)
      return
    }

    setLoading(true)
    if (key != 'data') {
      if (orgData[key] == input.value) {
        setLoading(false)
        return
      }
      if (key === 'parcelNo') {
        const reqBody = {
          [key]: input.value,
        }
        await updateLoanFields(reqBody, !!loanDetail.templateNumber)
        dispatch(setLoanData({ key, data: input.value }))
      } else {
        await updateReviewForm(currentId, key, input.value)
        if (key === 'productCode') {
          await updateReviewForm(currentId, 'appraiserAsIsValuationType', elingtonValuationTypes[input.value])
        } else if (key === 'appraiserAsIsValuationType') {
          if (reversedEllingtonValuationTypes[input.value])
            await updateReviewForm(currentId, 'productCode', reversedEllingtonValuationTypes[input.value])
        }
      }
      setOrgData({ ...orgData, [key]: input.value })
    }
    setLoading(false)

    if (key == 'title') {
      const newMap = cloneDeep(addressMap)
      const index = Object.keys(addressMap).indexOf(`${currentId}`)
      newMap[`${currentId}`] = `${index + 1}. ${input.value}`
      setAddressMap(newMap)
    }
  }

  const onShowHistory = (key: string) => {
    // parcelNo is from Loan table, not Appraisal Review
    if (key === 'parcelNo') {
      const options: Record<string, any> = {
        table: 'Loan',
        field: key,
        fieldLabel: inputs[key]?.title || key,
        keys: { field: key },
      }
      if (inputs[key]?.options) options.renderValue = inputs[key].options
      openAuditLog(options)
      return
    }

    const options: Record<string, any> = {
      table: 'Valuation Review',
      field: key,
      fieldLabel: inputs[key]?.title || key,
      keys: { id: currentId, field: key, loanNumber: overviewData.loanNumber },
    }
    if (inputs[key]?.options) options.map = inputs[key].options
    openAuditLog(options)
  }

  const onImportAppraiser = async (appraiser: Record<string, any>) => {
    const newInputs = cloneDeep(inputs)

    // Map appraiser data to review form fields
    const fieldMapping: Record<string, string> = {
      appraisalCompany: 'companyName',
      appraiserName: 'fullName',
      appraiserPhone: 'phone',
      appraiserEmail: 'email',
      appraiserAddress: 'address',
      appraiserLicense: 'licenseNumber',
    }

    setLoading(true)
    for (const [formKey, appraiserKey] of Object.entries(fieldMapping)) {
      if (newInputs[formKey] && appraiser[appraiserKey]) {
        newInputs[formKey].value = appraiser[appraiserKey]
        newInputs[formKey].error = ''
        // Save to backend
        await updateReviewForm(currentId, formKey, appraiser[appraiserKey])
      }
    }
    setInputs(newInputs)
    setLoading(false)
    toast('Appraiser data imported successfully', { type: 'success' })
  }

  const onAdd = (category: ReviewFormCategory) => {
    setShowAddModal(true)
    setSelectedData({
      categoryId: category.id,
    } as any)
  }

  const onClose = () => {
    setSelectedData(null)
    setShowAddModal(false)
  }

  const onEdit = (item: ReviewFormData) => {
    setShowAddModal(true)
    setSelectedData(item)
  }

  const onAskPrompt = async (data: ReviewFormData[], itemId: number, onChange: Function) => {
    const index = data.findIndex((v: ReviewFormData) => v.id == itemId)
    const newData = cloneDeep(data)
    const item = newData[index]
    item.aiHistory = []
    item.aiPrompt = item.comment ? '' : fillReviewFormText(item.description, placeholderValues)
    onChange(newData)
  }

  const onRemove = async (data: ReviewFormData[], itemId: number, onChange: Function) => {
    const index = data.findIndex((v: ReviewFormData) => v.id == itemId)

    const content = (
      <div className="text-gray-600 mb-4 text-md">
        Are you sure want to delete this item?
        <div className="text-gray-900 flex flex-col text-left text-sm">
          <span>Title: {fillReviewFormText(data[index].title, placeholderValues)}</span>
          <span>Description: {fillReviewFormText(data[index].description, placeholderValues)}</span>
        </div>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    const newData = cloneDeep(data)
    const removedData = newData[index]

    const isFromTemplate = !!removedData.no
    if (isFromTemplate) removedData.isDeleted = true
    else newData.splice(index, 1)

    setLoading(true)
    if (isFromTemplate) await updateReviewFormData(currentId, { update: removedData })
    else await updateReviewFormData(currentId, { remove: removedData })

    onChange(newData)
    setLoading(false)
  }

  const onSubmitModal = async (item: ReviewFormData) => {
    const newData: ReviewFormData[] = cloneDeep(inputs.data.value)
    setLoading(true)

    if (item.id) {
      const index = newData.findIndex((v: ReviewFormData) => v.id == item.id)
      newData[index] = item
      await updateReviewFormData(currentId, { update: item })
    } else {
      item.id = Date.now()
      newData.push(item)
      await updateReviewFormData(currentId, { add: item })
    }
    onChange('data', newData)
    setLoading(false)
  }

  const onUpdateData = useCallback(
    async (
      data: ReviewFormData[] = [],
      itemId: number,
      updatingData: Record<string, any>,
      onChange: Function,
      isSaving = false,
    ) => {
      const newData: ReviewFormData[] = cloneDeep(data)
      const index = newData.findIndex((v: ReviewFormData) => v.id == itemId)
      newData[index] = {
        ...newData[index],
        ...updatingData,
      }
      if (isSaving) {
        setLoading(true)
        await updateReviewFormData(currentId, { update: newData[index] })
      }
      onChange(newData)
      setLoading(false)
    },
    [inputs, currentId],
  )

  const onCopyAllAiFeedback = useCallback(
    async (data: ReviewFormData[], onChange: Function) => {
      const newData: ReviewFormData[] = cloneDeep(data)
      const toUpdate: ReviewFormData[] = []

      for (const item of newData) {
        if (item.aiHistory && item.aiHistory.length > 0) {
          const lastAiItem = [...item.aiHistory].reverse().find((h) => h.role === 'model')
          if (lastAiItem) {
            item.comment = lastAiItem.text
            toUpdate.push(item)
          }
        }
      }

      if (!toUpdate.length) return

      setLoading(true)
      await updateReviewFormData(currentId, { updateMany: toUpdate })
      onChange(newData)
      setLoading(false)
    },
    [currentId],
  )

  const onGenerate = async () => {
    let hasError = false
    const newStats = cloneDeep(inputs)
    const data: Record<string, any> = {}
    const reportsObj: Record<string, { check: boolean; info: string }> = {}
    for (const key in newStats) {
      if (newStats[key].visible === false) continue
      const { value, disabled = false } = newStats[key]
      let error = InputValidate(newStats[key])
      newStats[key].error = error
      if (error.length > 0) hasError = true
      if (disabled || value === undefined) continue
      if (key.startsWith('__report_check__')) {
        const id = key.slice('__report_check__'.length)
        reportsObj[id] = { ...(reportsObj[id] || { check: false, info: '' }), check: !!value }
      } else if (key.startsWith('__report_info__')) {
        const id = key.slice('__report_info__'.length)
        reportsObj[id] = { ...(reportsObj[id] || { check: false, info: '' }), info: (value as string) || '' }
      } else {
        data[key] = value
      }
    }
    data.reports = reportsObj
    if (hasError) {
      setInputs(newStats)
      toast(INVALID_ALL_INPUTS, { type: 'error' })
      return
    }
    if (overviewData.loanNumber === undefined) {
      return toast('Try again!', { type: 'error' })
    }
    data.loanNumber = `${overviewData.loanNumber}`
    if (Array.isArray(data.data)) {
      data.data = data.data.map((item: ReviewFormData) => ({
        ...item,
        title: fillReviewFormText(item.title, placeholderValues),
        description: fillReviewFormText(item.description, placeholderValues),
      }))
    }

    setLoading(true)

    const pdfData: Blob = await getAppraisalReviewPdf({
      categories,
      data,
      inputs,
      isUploadStore,
    })
    downloadFile(`${COMPANY_NAME_FC} Valuation Review - ${data.loanNumber}.pdf`, pdfData)
    setUploadStore(false)
    setLoading(false)
  }

  const renderAiHistory = (data: ReviewFormData[], itemId: number, onChange: Function) => {
    const index = data.findIndex((v: ReviewFormData) => v.id == itemId)
    const item = data[index]

    return (
      <AIPromptHistory
        identifier={`ReviewForm-${fillReviewFormText(item.title, placeholderValues)}`}
        baseFiles={orgData.lastAutofillFiles || []}
        history={item.aiHistory || []}
        prompt={item.aiPrompt || ''}
        files={item.aiFiles || []}
        filePath={`loan/${overviewData.loanNumber}/underwriting/review-form/prompts/${item.id}`}
        onUpdateHistory={(history) =>
          onUpdateData(data, item.id, { aiHistory: history, aiPrompt: '', aiFiles: [] }, onChange, true)
        }
        onUpdatePrompt={(prompt) => onUpdateData(data, item.id, { aiPrompt: prompt }, onChange, false)}
        onBlurPrompt={() => onUpdateData(data, item.id, {}, onChange, true)}
        onUpdateFiles={(files) => onUpdateData(data, item.id, { aiFiles: files }, onChange, true)}
      />
    )
  }

  const renderCategory = (category: ReviewFormCategory, data: ReviewFormData[] = [], onChange: Function) => {
    const items = data
      .filter((item) => item.categoryId == category.id && !item.isDeleted)
      .sort((a, b) => (a.order !== b.order ? (a.order > b.order ? 1 : -1) : a.title.localeCompare(b.title)))

    return items.map((item: ReviewFormData, index: number) => (
      <div className="p-3 border-b-2 border-black-100" key={`item-${item.id}-${index}`}>
        <div className="flex justify-between">
          <Checkbox
            id={`item-check-${item.id}-${index}`}
            title={fillReviewFormText(item.title, placeholderValues)}
            checked={item.isChecked || false}
            value={item.isChecked || false}
            onChange={(value) => onUpdateData(data, item.id, { isChecked: value }, onChange, true)}
          />
          {isAdmin && (
            <div className="flex gap-1">
              <Tooltip message="Edit">
                <button className="btn-icon" onClick={() => onEdit(item)}>
                  <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
                </button>
              </Tooltip>
              {canAiReview && !item.aiHistory && (
                <Tooltip message="Ask to AI">
                  <button className="btn-icon" onClick={() => onAskPrompt(data, item.id, onChange)}>
                    <CloudArrowUpIcon className="w-4 h-4" />
                  </button>
                </Tooltip>
              )}
              <Tooltip message="Delete">
                <button className="btn-icon !text-red-800" onClick={() => onRemove(data, item.id, onChange)}>
                  <TrashIcon className="w-4 h-4"></TrashIcon>
                </button>
              </Tooltip>
            </div>
          )}
        </div>
        <p className="text-black-500 text-[14px] italic mb-2">
          {fillReviewFormText(item.description, placeholderValues)}
        </p>
        {canAiReview && !!item.aiHistory && renderAiHistory(data, item.id, onChange)}
        <TextArea
          rows={2}
          title="Comment"
          value={item.comment}
          additionalElements={
            canAiReview && !!item.aiHistory && item.aiHistory.length > 0 ? (
              <Tooltip message="Copy AI Feedback">
                <button
                  className="btn-icon"
                  onClick={() => {
                    const lastAiItem = [...item.aiHistory!].reverse().find((h) => h.role === 'model')
                    if (lastAiItem) onUpdateData(data, item.id, { comment: lastAiItem.text }, onChange, true)
                  }}
                >
                  <ClipboardDocumentIcon className="w-4 h-4" />
                </button>
              </Tooltip>
            ) : undefined
          }
          onChange={(value) => onUpdateData(data, item.id, { comment: value }, onChange, false)}
          onBlur={() => onUpdateData(data, item.id, {}, onChange, true)}
        />
      </div>
    ))
  }

  const renderItems = useCallback(
    (value: ReviewFormData[] = [], onChange: Function) => {
      if (!categories) return <div />

      return (
        <>
          {canAiReview && (
            <div className="flex items-center justify-between">
              <Button onClick={() => setShowAIAutoFillModal(true)} link>
                <div className="flex items-center gap-1">
                  <CloudArrowUpIcon className="w-4 h-4" />
                  <span className="text-sm">AI Review</span>
                </div>
              </Button>
              <Button onClick={() => onCopyAllAiFeedback(value, onChange)} link>
                <div className="flex items-center gap-1">
                  <ClipboardDocumentIcon className="w-4 h-4" />
                  <span className="text-sm">Copy All AI Feedback to Comments</span>
                </div>
              </Button>
            </div>
          )}
          <div>
            {categories
              .filter((category) => category.visible)
              .map((category) => {
                const count = value.filter((item) => item.categoryId == category.id && !item.isDeleted).length
                return (
                  <div className="mb-3" key={`category-${category.id}`}>
                    <div className="flex items-baseline border-b-2 border-shade-blue px-3">
                      <div className="flex-1 font-semibold text-[17px]">
                        {category.value} ({count})
                      </div>

                      <button
                        className="flex hover-shadow1 p-1 cursor-pointer text-shade-blue items-center rounded m-2"
                        onClick={() => onAdd(category)}
                      >
                        <PlusIcon className="w-4 h-4"></PlusIcon>
                        <span className="text-sm">Add</span>
                      </button>
                    </div>
                    <div>{renderCategory(category, value, onChange)}</div>
                  </div>
                )
              })}
          </div>
        </>
      )
    },
    [categories, currentId, loadingByItem, onCopyAllAiFeedback, placeholderValues],
  )

  const loanOverviewDataFeed = (data: any) => {
    setOverviewData(data)
  }

  const onAiAutoFillClose = (success: boolean) => {
    setShowAIAutoFillModal(false)

    if (!success) return

    fetchReviewForm(Object.keys(addressMap).indexOf(`${currentId}`))
  }

  return (
    <div className={`ReviewForm-container ${!child && 'py-6 px-2'}`}>
      <div className={`${child && 'hidden'}`}>
        <Overview title="Valuation Review" feedOverview={loanOverviewDataFeed} noChangeTitle={child} />
      </div>
      <div className="max-w-screen-2xl m-auto">
        <div className={`relative bg-white ${!child && 'shadow1 rounded mb-4 p-6'}`}>
          <LayoutLoading show={loading} />
          <h2 className="text-2xl flex items-center gap-1 font-bold mb-2">
            <span>Valuation Review</span>
            {loading && <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin ml-2" />}
          </h2>

          <div className="review-form-container overflow-show">
            <div className="flex items-center justify-between mb-4 flex-4">
              {!!formCount && (
                <ButtonGroup
                  title={addressMap}
                  value={`${currentId}`}
                  onChange={(v) => fetchReviewForm(Object.keys(addressMap).indexOf(`${v}`))}
                />
              )}
              {!!formCount && (
                <div className="flex items-center flex-4">
                  <Button onClick={onCreateForm}>Add</Button>
                  <Button color="red" disabled={formCount == 1} onClick={onDeleteForm}>
                    Delete
                  </Button>
                </div>
              )}
            </div>
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mb-5">
              {Object.keys(inputs).map((key, index) => {
                const input = inputs[key]
                if (input.visible === false) return null
                if (loan.transactionType !== 'Purchase' && ['appraiserLTC', 'appraiserLTP'].includes(key)) {
                  return null
                }
                if (key == 'data') (input as any).render = renderItems

                // Add "Import Appraiser" button next to Appraiser Name
                // Add "Add to Appraiser" button next to Appraisal Company Name
                let additionalElements = null
                if (key === 'appraiserName') {
                  additionalElements = (
                    <div className="flex">
                      <span
                        className="!text-shade-blue flex items-center gap-1 cursor-pointer hover:underline"
                        onClick={() => setShowImportAppraiserModal(true)}
                        title="Import Appraiser"
                      >
                        <ArrowDownTrayIcon className="w-4 h-4" />
                        <span className="text-xs">Import Appraiser</span>
                      </span>
                    </div>
                  )
                } else if (key === 'appraisalCompany') {
                  additionalElements = (
                    <div className="flex">
                      <span
                        className="!text-shade-blue flex items-center gap-1 cursor-pointer hover:underline"
                        onClick={() => setShowAddAppraiserModal(true)}
                        title="Add to Appraiser"
                      >
                        <UserPlusIcon className="w-4 h-4" />
                        <span className="text-xs">Add to Appraiser</span>
                      </span>
                    </div>
                  )
                }

                const limits = calculateLoanLimits(loan, false, null, {
                  asIsValue: inputs?.appraiserAsIsProperty?.value || 0,
                  afterRepairValue: inputs?.appraiserAfterRepairProperty?.value || 0,
                })
                const finiteLimit = (value: number) => (Number.isFinite(value) ? value : '')
                if (key === 'appraiserAIV_LTV') {
                  input.value = finiteLimit(limits.aiv_ltv)
                }
                if (key === 'appraiserARV_LTV') {
                  input.value = finiteLimit(limits.arv_ltv)
                }
                if (key === 'appraiserLTC') {
                  input.value = finiteLimit(limits.ltc)
                }
                if (key === 'appraiserLTP') {
                  input.value = finiteLimit(limits.ltp)
                }

                return (
                  <div
                    className={`input col-span-1 ${input.span && 'md:col-span-2 lg:col-span-3'} items-center`}
                    key={`item-${index}-${key}`}
                  >
                    <RenderInput
                      input={{ ...input, additionalElements: additionalElements || input.additionalElements }}
                      Key={key}
                      onChange={onChange}
                      onBlur={onBlur}
                      showHistory={onShowHistory}
                    />
                  </div>
                )
              })}
            </div>
            <div className="">
              <div className="w-96 mx-auto py-3">
                <Toggle
                  id="upload-pdf-store"
                  title="Upload to Document Storage"
                  value={isUploadStore}
                  onChange={(value) => setUploadStore(value)}
                />
              </div>

              <div className="w-96 mx-auto">
                <Button full onClick={onGenerate}>
                  Generate Document
                </Button>
              </div>
            </div>
          </div>

          {isShowAddModal && categories && (
            <AddReviewFormModal
              categories={categories}
              data={selectedData}
              isDuplicate={(id: number, title: string) => isTitleDuplicate(id, title)}
              onClose={() => onClose()}
              onSubmit={(values: ReviewFormData) => onSubmitModal(values)}
            />
          )}

          {isShowAddAppraiserModal && (
            <AddAppraiserFromReviewModal
              onClose={() => setShowAddAppraiserModal(false)}
              reviewFormId={currentId}
              loanNumber={Number(overviewData.loanNumber ?? loan?.no) || 0}
              prefillData={{
                companyName: inputs.appraisalCompany?.value as string,
                fullName: inputs.appraiserName?.value as string,
                phone: inputs.appraiserPhone?.value as string,
                email: inputs.appraiserEmail?.value as string,
                address: inputs.appraiserAddress?.value as string,
                licenseNumber: inputs.appraiserLicense?.value as string,
              }}
            />
          )}

          {isShowImportAppraiserModal && (
            <ImportAppraiserModal onClose={() => setShowImportAppraiserModal(false)} onSelect={onImportAppraiser} />
          )}

          {isShowAIAutoFillModal && <AIAutoFillModal id={currentId} onClose={onAiAutoFillClose} />}
        </div>
      </div>
    </div>
  )
}

export default ReviewForm
